import { useState } from "react";
import { Globe, Copy, ArrowSquareOut, PencilSimple, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { openPdf } from "../../pages/platform/ResellerDetailPage";
import { PartnerPhoto } from "./PartnerPhoto";

/** Reseller portal: their public partner page (fixlr.app/partner/CODE) with editable headline/intro, plus the welcome pack. */
export function PartnerPageCard({ me, reload }) {
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const pp = me.partner_page;
  const codes = me.codes.filter((c) => c.active);
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Link copied"); };
  const save = async () => {
    setBusy(true);
    try { await API.put("/reseller-portal/profile", { headline: edit.headline, intro: edit.intro }); toast.success("Partner page updated"); setEdit(null); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    setBusy(false);
  };
  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="reseller-partner-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Globe size={18} className="text-primary" /><h2 className="font-bold font-[Chivo]">Your partner page</h2></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => openPdf("/reseller-portal/welcome-pack.pdf")} data-testid="reseller-pack-download"><FilePdf size={14} className="mr-1" /> Welcome pack</Button>
          <Button variant="outline" size="sm" onClick={() => setEdit({ headline: pp.custom_headline, intro: pp.custom_intro })} data-testid="reseller-partner-edit"><PencilSimple size={14} className="mr-1" /> Edit wording</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Share this link — it shows the customer their discount and drops them straight into sign-up with your code applied.</p>
      <div className="rounded-lg border border-border bg-muted/30 p-3 flex flex-wrap items-start gap-4">
        <PartnerPhoto photoUrl={me.photo_url} endpoint="/reseller-portal/photo" onChanged={reload} size={64} testId="reseller-photo" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm" data-testid="reseller-partner-headline">{pp.headline}</p>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2" data-testid="reseller-partner-intro">{pp.intro}</p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {codes.length === 0 && <li className="text-sm text-muted-foreground">Create a code above to get your first share link.</li>}
        {codes.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`reseller-partner-link-${c.code}`}>
            <span className="font-mono text-xs font-bold w-16">{c.code}</span>
            <a href={c.partner_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-full" data-testid={`reseller-partner-url-${c.code}`}>{c.partner_url.replace(/^https?:\/\//, "")}</a>
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => copy(c.partner_url)} data-testid={`reseller-partner-copy-${c.code}`}><Copy size={13} /></button>
            <a href={c.partner_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground" data-testid={`reseller-partner-open-${c.code}`}><ArrowSquareOut size={13} /></a>
          </li>
        ))}
      </ul>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="reseller-partner-dialog">
          <DialogHeader><DialogTitle>Your partner page wording</DialogTitle><DialogDescription>Shown to anyone who opens your link. Leave a field blank to use the standard text.</DialogDescription></DialogHeader>
          {edit && <div className="space-y-3">
            <div><Label className="text-xs">Headline</Label><Input value={edit.headline} onChange={(e) => setEdit({ ...edit, headline: e.target.value })} maxLength={pp.headline_max} placeholder={pp.headline} className="mt-1" data-testid="reseller-partner-headline-input" /><p className="text-[10px] text-muted-foreground mt-0.5">{edit.headline.length}/{pp.headline_max}</p></div>
            <div><Label className="text-xs">Short intro (2–3 sentences)</Label><Textarea value={edit.intro} onChange={(e) => setEdit({ ...edit, intro: e.target.value })} maxLength={pp.intro_max} rows={4} placeholder={pp.intro} className="mt-1" data-testid="reseller-partner-intro-input" /><p className="text-[10px] text-muted-foreground mt-0.5">{edit.intro.length}/{pp.intro_max}</p></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} disabled={busy} data-testid="reseller-partner-save">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
